<template>
  <v-container fluid class="mx-4 mt-3 px-8">
    <v-row justify="start" align="center">
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.year_batch"
          :items="batches"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Batch"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.program_id"
          :items="programs"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Programme"
          :item-text="
            (item) =>
              `${item.degree.short_name} ${
                item.specialization ? `- ${item.specialization}` : ''
              }`
          "
          item-value="id"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.partner_id"
          :items="partners"
          item-text="name"
          item-value="id"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Partner"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-text-field
          v-model="form.usn_id"
          outlined
          dense
          clearable
          hide-details="auto"
          label="USN"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-text-field
          v-model="form.employee_id"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Employee ID"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.approval_status"
          :items="['Pending', 'Approved']"
          outlined
          dense
          hide-details="auto"
          clearable
          label="Status"
          item-text="name"
          item-value="id"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="4" class="d-flex justify-start">
        <v-btn
          class="accent white--text"
          :loading="loading"
          @click="fetchReport()"
          :disabled="loading"
        >
          Search
        </v-btn>
        <v-btn
          :disabled="!studentExitData.length"
          class="accent white--text ml-2"
          @click="download()"
        >
          <v-icon>mdi-download</v-icon> Download
        </v-btn>
        <v-btn
          v-if="showSelect"
          class="success white--text ml-2"
          :disabled="!selectedItems.length"
          @click="showPartnerConfirmation('Approved')"
        >
          Approve
        </v-btn>
      </v-col>
    </v-row>
    <v-row class="mt-3">
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            v-model="selectedItems"
            :headers="exitStudentHeaders"
            :items="studentExitData"
            item-key="id"
            :show-select="showSelect"
            fixed-header
            :show-select-all="true"
          >
            <template v-slot:no-data>
              <div class="text-center">No data available</div>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
              <v-icon
                v-if="item.remarks"
                small
                class="mr-2"
                color="accent"
                @click="viewRemarks(item.remarks)"
              >
                mdi-eye
              </v-icon>
              <div v-else>-</div>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>

    <v-dialog v-model="adminConfirmationDialog" width="500" persistent>
      <confirmationDialogForm
        v-if="adminConfirmationDialog"
        :confirmationText="confirmationText"
        @closeDialog="adminConfirmationDialog = false"
        @saveDialog="updateStudentExitStatus"
      />
    </v-dialog>
    <v-dialog v-model="showRemarksDialog" max-width="600" persistent scrollable>
      <v-card class="secondary">
        <v-card-title class="primary white--text">
          Remarks
          <v-spacer></v-spacer>
          <v-btn small icon @click="showRemarksDialog = false">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="mt-3 reset-card-text">
          {{ remarks }}
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import confirmationDialogForm from "@/components/confirmationDialog.vue";
import { rules } from "@/helpers/rules";
import { exitStudentHeaders } from "@/constants/headers";

export default {
  components: {
    confirmationDialogForm,
  },
  data() {
    return {
      form: {
        approval_status: "Pending",
      },
      studentExitData: [],
      exitStudentHeaders,
      selectedItems: [],
      tab: 0,
      programs: [],
      users: [],
      adminConfirmationDialog: false,
      rules,
      currentStatus: "",
      partners: [],
      batches: [],
      loading: false,
      statuses: [],
      showRemarksDialog: false,
      remarks: "",
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
  },
  watch: {},
  async created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Exit Approvals" }]);

    Promise.all([this.init(), this.fetchReport()]);
  },
  methods: {
    init() {
      Promise.all([
        this.getPrograms(),
        this.getUsers(),
        this.getPartners(),
        this.generateBatchList(),
      ]);
    },
    async generateBatchList() {
      try {
        const response = await this.$axios.get("/get_year_batch");
        this.batches = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getPartners() {
      try {
        const response = await this.$axios.get("/partners");
        this.partners = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async fetchReport() {
      this.loading = true;
      this.showSelect = this.form.approval_status == "Pending";
      const queryString = this.objectToQueryParams({
        ...this.form,
      });
      await this.$axios
        .get(`/list_student_exit_requests?${queryString}`)
        .then((res) => {
          this.studentExitData = res.data.data;
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response,
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        })
        .finally(() => (this.loading = false));
    },
    objectToQueryParams(obj) {
      if (obj.year_batch) {
        const [year, batch] = obj.year_batch.split("-");
        obj.year = year;
        obj.batch = batch;
        delete obj.year_batch;
      }
      return Object.entries(obj)
        .filter(
          ([_, value]) => value !== undefined && value !== null && value !== "",
        )
        .map(
          ([key, value]) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
        )
        .join("&");
    },
    showPartnerConfirmation(status) {
      const showStatus = status === "Approved" ? "approve" : "reject";
      this.confirmationText = `Are you sure you want to ${showStatus} the selected ${
        this.selectedItems.length > 1 ? "students" : "student"
      }?`;
      this.adminConfirmationDialog = true;
      this.currentStatus = status;
    },
    updateStudentExitStatus() {
      const message =
        this.selectedItems.length > 1
          ? `Students ${this.currentStatus} successfully`
          : `Student ${this.currentStatus} successfully`;
      const itemIds = this.selectedItems.map((item) => item.id);
      this.updateAdminApprovalStatus(itemIds, this.currentStatus, message);
    },
    async updateAdminApprovalStatus(ids, status, message) {
      this.$axios
        .put(`/update_student_exit_status_by_admin/`, {
          status: status,
          ids,
        })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message,
            color: "success",
          });
          this.selectedItems = [];
          this.fetchReport();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response,
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        })
        .finally(() => (this.adminConfirmationDialog = false));
    },
    fetchDataOnTabChange() {
      this.selectedItems = [];
      this.fetchReport();
    },
    async getUsers() {
      try {
        const response = await this.$axios.get("/users");
        this.users = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getPrograms() {
      try {
        const url =
          this.currentUser.role === "Partner"
            ? `/get_partner_programs/${this.currentUser.partner_id}`
            : "/programs";
        const response = await this.$axios.get(url);
        this.programs = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async download() {
      this.$axios
        .get("/download_exit_requests", {
          params: {
            ...this.form,
          },
          responseType: "blob",
        })
        .then((res) => {
          let blob = new Blob([res.data], { type: "text/csv" }),
            url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "Admin Exit Approval.csv");
          document.body.appendChild(link);
          link.click();
        });
    },
    viewRemarks(remarks) {
      this.remarks = remarks;
      this.showRemarksDialog = true;
    },
  },
};
</script>

<style scoped></style>
